import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import PublicLayout from "@/components/PublicLayout";
import api, { formatApiError } from "@/lib/api";

export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && typeof user === "object") {
    const to = loc.state?.from || (user.role === "admin" ? "/admin" : "/dashboard");
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) setErr(res.error);
    else navigate(res.user.role === "admin" ? "/admin" : "/dashboard");
  };

  return (
    <PublicLayout>
      <section className="min-h-[calc(100vh-80px)] grid lg:grid-cols-2">
        <div className="hidden lg:block relative bg-[var(--ink)]">
          <img src="https://images.pexels.com/photos/17741066/pexels-photo-17741066.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900" alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
          <div className="relative z-10 h-full flex flex-col justify-between p-12 text-[var(--parchment)]">
            <div className="eyebrow text-[var(--brass)]">Sign in</div>
            <div>
              <blockquote className="font-display italic text-3xl leading-snug max-w-md">
                "We replaced four research subscriptions with one Luminary seat. The board notices."
              </blockquote>
              <div className="mt-5 text-sm font-mono uppercase tracking-[0.22em] text-[var(--parchment)]/65">
                Chief Strategy Officer · Specialty Chemicals
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center p-8 lg:p-16 bg-[var(--parchment)]">
          <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
            <div className="eyebrow">Welcome back</div>
            <h1 className="font-display font-black text-4xl mt-3">Sign in to Luminary.</h1>
            <div className="mt-10 space-y-5">
              <div>
                <label className="label-mono">Email</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-editorial" data-testid="login-email-input" />
              </div>
              <div>
                <label className="label-mono">Password</label>
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-editorial" data-testid="login-password-input" />
              </div>
              {err && <div className="text-sm font-mono text-red-700" data-testid="login-error">{err}</div>}
              <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="login-submit-button">
                {busy ? "Signing in…" : "Sign in"}
              </button>
              <div className="flex items-center justify-between text-sm">
                <Link to="/forgot-password" className="font-mono text-xs uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--oxblood)]" data-testid="forgot-link">Forgot password</Link>
                <Link to="/register" className="font-mono text-xs uppercase tracking-[0.22em] text-[var(--oxblood)] hover:underline" data-testid="register-link">Create account →</Link>
              </div>
            </div>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
}

export function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && typeof user === "object") return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    const res = await register(email, password, name);
    setBusy(false);
    if (!res.ok) setErr(res.error);
    else navigate("/dashboard");
  };

  return (
    <PublicLayout>
      <section className="min-h-[calc(100vh-80px)] grid lg:grid-cols-2">
        <div className="hidden lg:block relative bg-[var(--ink)]">
          <img src="https://images.pexels.com/photos/10432212/pexels-photo-10432212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900" alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
          <div className="relative z-10 h-full flex flex-col justify-between p-12 text-[var(--parchment)]">
            <div className="eyebrow text-[var(--brass)]">Join Luminary</div>
            <div>
              <blockquote className="font-display italic text-3xl leading-snug max-w-md">
                "Sourced, scored, and ready for the next board meeting."
              </blockquote>
              <div className="mt-5 text-sm font-mono uppercase tracking-[0.22em] text-[var(--parchment)]/65">— The Luminary standard</div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center p-8 lg:p-16 bg-[var(--parchment)]">
          <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
            <div className="eyebrow">Create account</div>
            <h1 className="font-display font-black text-4xl mt-3">Start with Luminary.</h1>
            <div className="mt-10 space-y-5">
              <div><label className="label-mono">Full name</label><input required value={name} onChange={(e) => setName(e.target.value)} className="input-editorial" data-testid="register-name-input" /></div>
              <div><label className="label-mono">Email</label><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-editorial" data-testid="register-email-input" /></div>
              <div><label className="label-mono">Password (min 8 chars)</label><input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="input-editorial" data-testid="register-password-input" /></div>
              {err && <div className="text-sm font-mono text-red-700">{err}</div>}
              <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="register-submit-button">
                {busy ? "Creating…" : "Create account"}
              </button>
              <div className="text-sm text-center">
                <span className="text-[var(--ink-soft)]">Already a member?</span>{" "}
                <Link to="/login" className="font-mono text-xs uppercase tracking-[0.22em] text-[var(--oxblood)] hover:underline">Sign in →</Link>
              </div>
            </div>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg(""); setToken("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message);
      if (data.reset_token) setToken(data.reset_token);
    } catch (err) { setMsg(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <PublicLayout>
      <section className="min-h-[calc(100vh-80px)] flex items-center justify-center p-8 bg-[var(--parchment)]">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="forgot-form">
          <div className="eyebrow">Recover</div>
          <h1 className="font-display font-black text-4xl mt-3">Reset your password.</h1>
          <p className="mt-3 text-sm text-[var(--ink-soft)]">We'll generate a reset token (email sending not yet connected).</p>
          <div className="mt-8 space-y-5">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-editorial" placeholder="you@firm.com" data-testid="forgot-email-input" />
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="forgot-submit-button">{busy ? "Working…" : "Send reset token"}</button>
            {msg && <p className="text-sm font-mono text-[var(--ink-soft)]">{msg}</p>}
            {token && (
              <div className="mt-2 p-3 bg-white border border-[var(--border-soft)]">
                <div className="eyebrow">Reset token (MVP)</div>
                <code className="break-all text-xs">{token}</code>
                <Link to="/reset-password" className="block mt-3 font-mono text-xs uppercase tracking-[0.22em] text-[var(--oxblood)] hover:underline">Continue to reset →</Link>
              </div>
            )}
            <Link to="/login" className="block text-center font-mono text-xs uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--oxblood)]">← Back to sign in</Link>
          </div>
        </form>
      </section>
    </PublicLayout>
  );
}

export function ResetPasswordPage() {
  const [token, setToken] = useState("");
  const [pw, setPw] = useState("");
  const [msg, setMsg] = useState("");
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setMsg("Password reset. Redirecting…");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) { setMsg(formatApiError(err)); }
  };
  return (
    <PublicLayout>
      <section className="min-h-[calc(100vh-80px)] flex items-center justify-center p-8 bg-[var(--parchment)]">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="reset-form">
          <div className="eyebrow">Set a new password</div>
          <h1 className="font-display font-black text-4xl mt-3">Reset.</h1>
          <div className="mt-8 space-y-5">
            <div><label className="label-mono">Token</label><input required value={token} onChange={(e) => setToken(e.target.value)} className="input-editorial" data-testid="reset-token-input" /></div>
            <div><label className="label-mono">New password</label><input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className="input-editorial" data-testid="reset-password-input" /></div>
            <button type="submit" className="btn-primary w-full justify-center" data-testid="reset-submit-button">Set password</button>
            {msg && <p className="text-sm font-mono text-[var(--ink-soft)]">{msg}</p>}
          </div>
        </form>
      </section>
    </PublicLayout>
  );
}
